<!-- Страница управления каталогом работ, запчастей и материалов для ремонта. -->
<template>
  <div class="catalog-items-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <!-- Верхняя панель страницы -->
      <base-nav
        title="Каталог"
        :show-back="true"
        back-fallback="Home"
        :back-fallback-pattern="/\/catalog/"
      >
        <base-primary-button icon="addSquare" class="nav-btn" @click="openCreateModal">
          Создать
        </base-primary-button>
      </base-nav>

      <!-- Фильтры каталога -->
      <FilterBlock
        :search-fields="searchFields"
        :initial-filters="appliedFilters"
        auto-search
        @filter-change="handleFilterChange"
      />

      <!-- Список позиций каталога -->
      <div v-if="catalogItems.length === 0" class="no-data">
        Каталог пуст
      </div>

      <ul v-else class="catalog-items-list">
        <BaseSettingsListItemCard
          v-for="catalogItem in catalogItems"
          :key="catalogItem.id"
          :enabled="!catalogItem.archived_at"
          :is-loading="isSubmitting"
          clickable
          @edit="openEditModal(catalogItem)"
          @primary-action="openEditModal(catalogItem)"
          @toggle="toggleCatalogItem(catalogItem)"
        >
          <div class="catalog-item-card">
            <div class="catalog-item-card__top">
              <span class="catalog-item-card__name">
                {{ catalogItem.name || "—" }}
              </span>
              <span
                v-if="catalogItem.number"
                class="catalog-item-card__number"
              >
                {{ catalogItem.number }}
              </span>
            </div>

            <div class="catalog-item-card__details">
              <span class="detail-chip">
                {{ formatBaseType(catalogItem.base_type) }}
              </span>
              <span class="detail-chip">
                {{ formatUsageScopes(catalogItem.usage_scopes) }}
              </span>
              <span
                v-if="catalogItem.unit_price || catalogItem.unit"
                class="detail-chip detail-chip--price"
              >
                {{ catalogItem.unit_price || "0" }} ₽
                <template v-if="catalogItem.unit">
                  / {{ catalogItem.unit }}
                </template>
              </span>
              <span class="detail-chip">
                {{ formatVatKind(catalogItem.vat_kind) }}
              </span>
              <span
                v-if="
                  catalogItem.vat_kind === 'with_vat' &&
                  catalogItem.vat_rate
                "
                class="detail-chip"
              >
                {{ catalogItem.vat_rate }}%
              </span>
              <span class="detail-chip">
                {{
                  formatVatCalculationType(
                    catalogItem.vat_calculation_type
                  )
                }}
              </span>
            </div>
          </div>
        </BaseSettingsListItemCard>
      </ul>

      <pagination-component
        v-if="pagination.pages > 1"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
        :route-query="currentRouteQuery"
      />
    </template>

    <!-- Модалка создания позиции -->
    <BaseModal
      :visible="showCreateModal"
      title="Создание позиции каталога"
      size="lg"
      adaptive-fullscreen
      elevated-footer
      @close="closeCreateModal"
    >
      <CatalogItemForm
        :form-id="createCatalogItemFormId"
        :show-actions="false"
        :base-type-options="baseTypeOptions"
        :usage-scope-options="usageScopeOptions"
        :vat-kind-options="vatKindOptions"
        :vat-calculation-type-options="vatCalculationTypeOptions"
        :is-loading="isSubmitting"
        @save="handleCreate"
        @cancel="closeCreateModal"
      />

      <template #footer>
        <base-primary-button
          type="submit"
          :form="createCatalogItemFormId"
          :disabled="isSubmitting"
          class="form-btn"
        >
          <span v-if="isSubmitting">Сохранение...</span>
          <span v-else>Сохранить</span>
        </base-primary-button>
        <base-button
          type="button"
          class="form-btn"
          :disabled="isSubmitting"
          @click="closeCreateModal"
        >
          Отмена
        </base-button>
      </template>
    </BaseModal>

    <!-- Модалка редактирования и архивирования позиции -->
    <BaseModal
      :visible="showEditModal"
      title="Редактирование позиции каталога"
      size="lg"
      adaptive-fullscreen
      class="catalog-item-edit-modal"
      footer-align="start"
      elevated-footer
      @close="closeEditModal"
    >
      <CatalogItemForm
        v-if="editingCatalogItem"
        :form-id="editCatalogItemFormId"
        :show-actions="false"
        :initial-data="editingCatalogItem"
        :base-type-options="baseTypeOptions"
        :usage-scope-options="usageScopeOptions"
        :vat-kind-options="vatKindOptions"
        :vat-calculation-type-options="vatCalculationTypeOptions"
        :is-loading="isSubmitting"
        @save="
          (payload) => handleUpdate(editingCatalogItem.id, payload)
        "
        @cancel="closeEditModal"
      />

      <template #footer>
        <base-primary-button
          type="submit"
          :form="editCatalogItemFormId"
          :disabled="catalogItemModalFooterIsDisabled"
          class="form-btn"
        >
          <span v-if="isSubmitting">Сохранение...</span>
          <span v-else>Сохранить</span>
        </base-primary-button>
        <base-button
          type="button"
          class="form-btn"
          :disabled="catalogItemModalFooterIsDisabled"
          @click="closeEditModal"
        >
          Отмена
        </base-button>
        <BaseArchiveRestoreButton
          v-if="editingCatalogItem"
          class="catalog-item-archive-btn"
          :archived="editingCatalogItemIsArchived"
          :disabled="catalogItemModalFooterIsDisabled"
          :loading="archiveActionIsLoading"
          archive-title="Архивировать позицию каталога?"
          archive-message="Позиция будет убрана из обычного списка. Ее можно будет восстановить из архива."
          restore-title="Восстановить позицию каталога?"
          restore-message="Позиция снова появится в обычном списке каталога."
          @click="handleArchiveAction"
        />
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";

import CatalogItemForm from "@/components/Catalog/ItemForm.vue";
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

import { useCatalogItemStore } from "@/stores/catalogItemStore";

import rawSearchFields from "@/config/filters/catalog/items.json";
import { catalogFiltersToApiParams } from "@/utils/catalogItemFilterUtils";
import {
  filtersToUrlQuery,
  getInitialFilters,
} from "@/utils/filterUtils";
import { removeQueryParams } from "@/utils/urlUtils";

export default {
  name: "CatalogItemsPage",

  components: {
    CatalogItemForm,
    FilterBlock,
    PaginationComponent,
  },

  data() {
    return {
      dataIsLoading: true,
      archiveActionIsLoading: false,
      createCatalogItemFormId: "catalog-item-create-form",
      editCatalogItemFormId: "catalog-item-edit-form",
      showCreateModal: false,
      showEditModal: false,
      editingCatalogItem: null,
      editingCatalogItemArchivedAt: null,
      appliedFilters: getInitialFilters(
        this.$route,
        rawSearchFields,
        {
          searchValues: {},
        }
      ),
    };
  },

  computed: {
    ...mapState(useCatalogItemStore, [
      "catalogItems",
      "isSubmitting",
      "pagination",
      "baseTypeOptions",
      "usageScopeOptions",
      "vatKindOptions",
      "vatCalculationTypeOptions",
    ]),

    searchFields() {
      return rawSearchFields;
    },

    editingCatalogItemIsArchived() {
      return Boolean(this.editingCatalogItemArchivedAt);
    },

    catalogItemModalFooterIsDisabled() {
      return this.isSubmitting || this.archiveActionIsLoading;
    },

    currentRouteQuery() {
      return this.$route.query;
    },
  },

  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.loadCatalogItems(this.$route.query.page);
      },
    },
  },

  methods: {
    ...mapActions(useCatalogItemStore, [
      "archiveCatalogItem",
      "createCatalogItem",
      "fetchCatalogItems",
      "formatBaseType",
      "formatUsageScopes",
      "formatVatCalculationType",
      "formatVatKind",
      "restoreCatalogItem",
      "updateCatalogItem",
    ]),

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        { searchValues: {} }
      );
    },

    async loadCatalogItems(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;

      this.dataIsLoading = true;
      try {
        const params = {
          page: pageNumber,
          ...catalogFiltersToApiParams(
            this.appliedFilters,
            this.searchFields
          ),
        };
        if (this.$route.query.catalog_item_id) {
          params["q[id]"] = this.$route.query.catalog_item_id;
          params["q[archived]"] = "all";
        }

        await this.fetchCatalogItems(params);
        this.openCatalogItemFromRoute();
      } finally {
        this.dataIsLoading = false;
      }
    },

    openCreateModal() {
      this.closeEditModal();
      this.showCreateModal = true;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    openEditModal(catalogItem) {
      this.editingCatalogItem = catalogItem;
      this.editingCatalogItemArchivedAt = catalogItem.archived_at;
      this.showEditModal = true;
      this.closeCreateModal();
    },

    openCatalogItemFromRoute() {
      const catalogItemId = this.$route.query.catalog_item_id;
      if (!catalogItemId || this.showEditModal) return;

      const catalogItem = this.catalogItems.find(
        (item) => String(item.id) === String(catalogItemId)
      );
      if (catalogItem) this.openEditModal(catalogItem);
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingCatalogItem = null;
      this.editingCatalogItemArchivedAt = null;
      removeQueryParams("catalog_item_id", this.$router, this.$route);
    },

    handleFilterChange(filters) {
      this.appliedFilters = filters;
      this.updateUrlWithCatalogFilters(filters);
    },

    updateUrlWithCatalogFilters(filters) {
      this.$router
        .replace({
          query: filtersToUrlQuery(filters, 1, this.searchFields),
        })
        .catch(() => {});
    },

    async handleCreate(payload) {
      await this.createCatalogItem(payload);
      this.closeCreateModal();
    },

    async handleUpdate(id, payload) {
      await this.updateCatalogItem(id, payload);
      this.closeEditModal();
    },

    async toggleCatalogItem(catalogItem) {
      let updatedCatalogItem;

      if (catalogItem.archived_at) {
        updatedCatalogItem = await this.restoreCatalogItem(
          catalogItem.id
        );
      } else {
        updatedCatalogItem = await this.archiveCatalogItem(
          catalogItem.id
        );
      }

      if (
        updatedCatalogItem &&
        String(this.editingCatalogItem?.id) ===
          String(updatedCatalogItem.id)
      ) {
        this.editingCatalogItemArchivedAt =
          updatedCatalogItem.archived_at;
      }
    },

    async handleArchiveAction() {
      if (!this.editingCatalogItem) return;

      this.archiveActionIsLoading = true;
      try {
        await this.toggleCatalogItem(this.editingCatalogItem);
      } finally {
        this.archiveActionIsLoading = false;
      }
    },
  },

  async created() {
    await this.loadCatalogItems(this.$route.query.page);
  },
};
</script>

<style scoped>
.catalog-items-container {
  flex-grow: 1;
}

.catalog-items-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-in-indent);
  margin: 0;
  padding: 0;
}

.catalog-item-card {
  min-width: 0;
}

.catalog-item-card__top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.catalog-item-card__name {
  font-size: 16px;
  font-weight: 700;
}

.catalog-item-card__number {
  color: #6b7280;
  font-size: 13px;
}

.catalog-item-card__details {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.detail-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
  line-height: 1.2;
}

.detail-chip--price {
  background: #ecfdf5;
  color: #047857;
}

.no-data {
  margin-top: 40px;
  color: #6b7280;
  text-align: center;
}

@media (max-width: 760px) {
  .catalog-item-edit-modal :deep(.base-modal__footer) {
    flex-direction: column;
  }

  .catalog-item-edit-modal :deep(.catalog-item-archive-btn) {
    align-self: center;
    width: auto;
    min-width: 180px;
    margin: 0;
  }

  .catalog-item-edit-modal :deep(.catalog-item-archive-btn button),
  .catalog-item-edit-modal
    :deep(.catalog-item-archive-btn .archive-restore-btn) {
    width: auto;
  }
}
</style>
